import React from "react";

// Server-renderable hero + supporting sections for /fathersday.
//
// This is the SSR fallback for FathersDayNarrativeBanner. The narrative
// banner uses `useSearchParams` (client-only), so Next.js renders its
// Suspense fallback in SSR HTML — crawlers and SEO scanners see whatever
// the fallback emits. This component owns all the static text the
// auditor needs: H1, intro copy, real outgoing anchor links (Browse +
// FAQ), and the two follow-on H2 sections.
//
// The same component is re-rendered inside the client banner after
// hydration, so the visual transition is seamless. The optional
// `onBrowseClick` / `onFaqClick` props let the client banner intercept
// the link clicks to trigger confetti, collapse, and smooth scroll —
// while the SSR render leaves them as plain `<a>` elements that work
// without JS and are crawlable as outbound links.

const PlayWithSparklesIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    className="h-12 w-12"
    aria-hidden="true"
  >
    <circle cx="24" cy="26" r="16" stroke="#6852c0" strokeWidth="2.5" fill="#FFFFFF" />
    <path
      d="M21 20 L21 32 L31 26 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M40 10 L40 16 M37 13 L43 13" stroke="#F5B947" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 8 L8 12 M6 10 L10 10" stroke="#F5B947" strokeWidth="2" strokeLinecap="round" />
    <circle cx="42" cy="32" r="1.5" fill="#F5B947" />
  </svg>
);

const PolaroidCameraIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    className="h-12 w-12"
    aria-hidden="true"
  >
    <rect x="6" y="14" width="36" height="28" rx="3" fill="#FFFFFF" stroke="#6852c0" strokeWidth="2.5" />
    <rect x="16" y="9" width="14" height="6" rx="1.5" fill="#FFFFFF" stroke="#6852c0" strokeWidth="2" />
    <circle cx="24" cy="28" r="8" fill="#6BC4D2" stroke="#6852c0" strokeWidth="2.5" />
    <circle cx="24" cy="28" r="3.5" fill="#FFFFFF" stroke="#6852c0" strokeWidth="1.5" />
    <rect x="33" y="18" width="5" height="3.5" rx="0.5" fill="#F5B947" />
  </svg>
);

const PaperPlaneIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    className="h-12 w-12"
    aria-hidden="true"
  >
    <path
      d="M42 8 L6 22 L20 27 L42 8 Z"
      fill="#FFFFFF"
      stroke="#6852c0"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M42 8 L20 27 L24 40 L42 8 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path d="M4 32 L12 32" stroke="#F5B947" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M8 38 L18 38" stroke="#F5B947" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M2 26 L8 26" stroke="#F5B947" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const SparkleBurstIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-7 w-7 flex-shrink-0 md:h-8 md:w-8"
    aria-hidden="true"
  >
    <path
      d="M16 3 L18 13 L28 16 L18 19 L16 29 L14 19 L4 16 L14 13 Z"
      fill="#F5B947"
      stroke="#6852c0"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="5" cy="5" r="1.5" fill="#F47C7C" />
    <circle cx="27" cy="5" r="1.5" fill="#F47C7C" />
    <circle cx="5" cy="27" r="1.5" fill="#6BC4D2" />
    <circle cx="27" cy="27" r="1.5" fill="#6BC4D2" />
  </svg>
);

const HeartIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-7 w-7 flex-shrink-0 md:h-8 md:w-8"
    aria-hidden="true"
  >
    <path
      d="M16 27 C4 18, 4 9, 11 9 C14 9, 16 12, 16 12 C16 12, 18 9, 21 9 C28 9, 28 18, 16 27 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="5" cy="6" r="1.5" fill="#F5B947" />
    <circle cx="27" cy="6" r="1.5" fill="#F5B947" />
  </svg>
);

const PaintbrushIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-8 w-8 flex-shrink-0"
    aria-hidden="true"
  >
    <path d="M4 10 Q 16 2, 28 9" stroke="#F47C7C" strokeWidth="3" strokeLinecap="round" fill="none" />
    <path
      d="M3 28 L6 31 L17 20 L14 17 Z"
      fill="#FFFCEB"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 17 L17 20 L20 17 L17 14 Z"
      fill="#F5B947"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M17 14 L20 17 L24 11 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const CouponIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-8 w-8 flex-shrink-0"
    aria-hidden="true"
  >
    <rect x="3" y="9" width="26" height="14" rx="1.5" fill="#FFFFFF" stroke="#6852c0" strokeWidth="1.5" />
    <line x1="11" y1="10" x2="11" y2="22" stroke="#6852c0" strokeWidth="1.5" strokeDasharray="2 2" strokeLinecap="round" />
    <path d="M7 13 L7 17 M5 15 L9 15" stroke="#F5B947" strokeWidth="2" strokeLinecap="round" />
    <path
      d="M20 17 C18 15.5, 15.5 16, 15.5 18 C15.5 20.5, 20 22, 20 22 C20 22, 24.5 20.5, 24.5 18 C24.5 16, 22 15.5, 20 17 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="1"
      strokeLinejoin="round"
    />
  </svg>
);

const SproutIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-8 w-8 flex-shrink-0"
    aria-hidden="true"
  >
    <circle cx="26" cy="6" r="2" fill="#F5B947" stroke="#6852c0" strokeWidth="1" />
    <path
      d="M16 26 Q9 22 7 14 Q13 14 16 23 Z"
      fill="#A8E0B5"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M16 26 Q23 22 25 14 Q19 14 16 23 Z"
      fill="#A8E0B5"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <line x1="16" y1="26" x2="16" y2="22" stroke="#6852c0" strokeWidth="2" strokeLinecap="round" />
    <path d="M5 28 Q16 25 27 28" stroke="#6852c0" strokeWidth="1.5" strokeLinecap="round" fill="none" />
  </svg>
);

interface FathersDayHeroProps {
  // When provided, the Browse CTA calls this instead of letting the
  // browser follow the #browse anchor. The client banner uses it to
  // also fire confetti and persist the collapsed state.
  onBrowseClick?: () => void;
  // When provided, intercepts the FAQ link click for smooth scroll
  // and reduced-motion handling.
  onFaqClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export default function FathersDayHero({
  onBrowseClick,
  onFaqClick,
}: FathersDayHeroProps = {}) {
  const handleBrowseClick = onBrowseClick
    ? (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onBrowseClick();
      }
    : undefined;

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-wg-purple text-white">
        <div className="mx-auto max-w-4xl px-6 py-12 text-center">
          <h1 className="mb-4 text-4xl font-bold md:text-5xl">
            Father&apos;s Day eCards
          </h1>
          <p className="mb-2 text-lg text-white/90 md:text-xl">
            Animated cards for dads, stepdads, granddads, and the father figures
            who showed up. Hand-crafted by real artists, sent in seconds, with
            room for your own photos and words.
          </p>
          <p className="mb-8 text-base text-white/80">
            Father&apos;s Day 2026 is <strong>Sunday, June 21</strong>. Digital
            cards land the morning of — no shipping window to miss.
          </p>
          <a
            href="#browse"
            onClick={handleBrowseClick}
            className="inline-block rounded-lg bg-white px-6 py-2 text-lg font-semibold text-wg-purple transition-colors hover:bg-gray-100"
          >
            Browse Father&apos;s Day Cards
          </a>
          <p className="mt-6 text-sm text-white/85">
            <a
              href="#faq"
              onClick={onFaqClick}
              className="inline-flex items-center gap-1 underline decoration-white/40 underline-offset-4 transition-colors hover:text-white hover:decoration-white"
            >
              Not sure what to write? Have a question? Read the FAQs
              <span aria-hidden="true">↓</span>
            </a>
          </p>
        </div>
      </section>

      {/* Why an animated card */}
      <section className="bg-wg-bg py-12">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="mb-8 flex flex-wrap items-center justify-center gap-3 text-center text-2xl font-bold text-wg-purple md:gap-4 md:text-3xl">
            <SparkleBurstIcon />
            <span>Why Send an Animated Father&apos;s Day Card?</span>
            <SparkleBurstIcon />
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4">
                <PlayWithSparklesIcon />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Movement Carries Feeling
              </h3>
              <p className="text-[#4a4a4a]">
                A still card says the words. An animated one delivers the
                feeling — perfect when you can&apos;t be in the room to hand it
                over yourself.
              </p>
            </div>
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4">
                <PolaroidCameraIcon />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Add Your Photos &amp; Voice
              </h3>
              <p className="text-[#4a4a4a]">
                Drop in the photo from that fishing trip, the video of the
                grandkids, the message in your own voice. Generic cards skip
                this part. We don&apos;t.
              </p>
            </div>
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4">
                <PaperPlaneIcon />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Arrives in Seconds
              </h3>
              <p className="text-[#4a4a4a]">
                No shipping cutoffs. Send it the morning of Father&apos;s Day —
                or the afternoon of, when you suddenly remember. Both work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-wg-bg py-10">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="mb-8 flex flex-wrap items-center justify-center gap-3 text-center text-2xl font-bold text-wg-purple md:gap-4 md:text-3xl">
            <HeartIcon />
            <span>What Makes Us Different</span>
            <HeartIcon />
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <PaintbrushIcon />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                  Designed by Real Artists
                </h3>
                <p className="text-[#4a4a4a]">
                  Every animation is handcrafted — not auto-generated. Our
                  artists draw, animate, and revise so the moment lands.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CouponIcon />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                  Free to Start
                </h3>
                <p className="text-[#4a4a4a]">
                  Browse the Father&apos;s Day collection, customize a card,
                  and send it without a paywall in the way of the moment.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <SproutIcon />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                  Zero Waste, Same Warmth
                </h3>
                <p className="text-[#4a4a4a]">
                  No paper, no envelope, no landfill — just the message and
                  the smile. A nice touch for the dad who&apos;s been
                  decluttering.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
